import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { sacredPath } from "@/lib/sacred-library/utils/registry";
import { useTattvaGraph } from "@/hooks/useTattvaGraph";

const LIBRARY_TYPES = new Set(["deity", "festival", "vrata", "story", "sadhana", "mantra"]);

const RELATION_LABELS = {
  has_chakra: "Chakra",
  has_element: "Element",
  has_guna: "Guṇa",
  governed_by_planet: "Planet",
  related_story: "Stories",
  related_sadhana: "Sādhanas",
  related_mantra: "Mantras",
  related_festival: "Festivals",
  ruled_by: "Ruling graha",
  ascends_to: "Energy flow",
  aligns_with: "Aligns with",
};

const TATTVA_SECTIONS = [
  { relation: "has_chakra", label: "Chakra" },
  { relation: "has_element", label: "Element" },
  { relation: "has_guna", label: "Guṇa" },
  { relation: "governed_by_planet", label: "Planet" },
  { relation: "related_story", label: "Related stories" },
  { relation: "related_sadhana", label: "Related sādhanas" },
  { relation: "related_mantra", label: "Related mantras" },
  { relation: "related_festival", label: "Related festivals" },
];

function nodeHref(node) {
  if (LIBRARY_TYPES.has(node.type) && node.library_ref) {
    return sacredPath(node.library_ref.type, node.library_ref.id);
  }
  if (LIBRARY_TYPES.has(node.type)) {
    const part = node.id.includes(":") ? node.id.split(":")[1] : node.id;
    return sacredPath(node.type, part);
  }
  return null;
}

function NodeChip({ node }) {
  const title = node.title || node.id.replace(/.*:/, "").replace(/_/g, " ");
  const href = nodeHref(node);
  if (href) {
    return (
      <Button variant="outline" size="sm" asChild className="capitalize">
        <Link to={href}>{title}</Link>
      </Button>
    );
  }
  return (
    <Badge variant="secondary" className="capitalize">
      {title}
    </Badge>
  );
}

function Section({ label, nodes }) {
  if (!nodes?.length) return null;
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
      <div className="flex flex-wrap gap-2">
        {nodes.map((n) => (
          <NodeChip key={n.id} node={n} />
        ))}
      </div>
    </div>
  );
}

export function TattvaConnections({ topicType, topicId }) {
  const { data, loading, error } = useTattvaGraph(topicType, topicId);

  if (loading) {
    return (
      <div className="flex items-center gap-2 pt-4 border-t mt-4 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-xs">Loading tattva connections…</span>
      </div>
    );
  }

  if (error || !data?.nodes?.length) return null;

  const byRelation = data.by_relation || {};
  const byType = data.by_type || {};

  const extraTypes = Object.entries(byType).filter(
    ([t]) => !["chakra", "element", "guna", "planet"].includes(t) && LIBRARY_TYPES.has(t)
  );

  const hasStructured = TATTVA_SECTIONS.some((s) => byRelation[s.relation]?.length);

  return (
    <div className="pt-4 border-t mt-4 space-y-4" data-testid="tattva-connections">
      <p className="text-xs text-muted-foreground w-full font-medium">Tattva & cross-links</p>

      {hasStructured ? (
        TATTVA_SECTIONS.map((s) => (
          <Section key={s.relation} label={s.label} nodes={byRelation[s.relation]} />
        ))
      ) : (
        Object.entries(byRelation).map(([rel, nodes]) => (
          <Section key={rel} label={RELATION_LABELS[rel] || rel.replace(/_/g, " ")} nodes={nodes} />
        ))
      )}

      {extraTypes.map(([type, nodes]) => (
        <Section
          key={type}
          label={type.charAt(0).toUpperCase() + type.slice(1) + "s"}
          nodes={nodes}
        />
      ))}
    </div>
  );
}
